import { useState, useEffect } from 'react';
import adminApi from '@/lib/adminApi';

interface SystemErrorIssue {
  title: string;
  culprit: string | null;
  level: string;
  count: number;
  lastSeen: string | null;
}

interface SystemErrorsData {
  configured: boolean;
  status: 'ok' | 'warning' | 'critical' | 'unknown';
  totalIssues: number;
  totalEvents24h: number;
  issues: SystemErrorIssue[];
  trackerUrl: string | null;
}

const STATUS_UI: Record<SystemErrorsData['status'], { icon: string; title: string; sub: string; box: string }> = {
  ok: {
    icon: '✅',
    title: 'Tudo funcionando',
    sub: 'Nenhum erro registrado nas últimas 24 horas.',
    box: 'border-green-200 bg-green-50',
  },
  warning: {
    icon: '⚠️',
    title: 'Alguns erros detectados',
    sub: 'O sistema está no ar, mas registramos erros recentes. Vale avisar a equipe técnica.',
    box: 'border-amber-200 bg-amber-50',
  },
  critical: {
    icon: '🚨',
    title: 'Volume alto de erros',
    sub: 'Muitos erros nas últimas 24 horas — acione a equipe técnica agora.',
    box: 'border-red-200 bg-red-50',
  },
  unknown: {
    icon: '❔',
    title: 'Monitoramento indisponível',
    sub: 'Não foi possível consultar o monitor de erros agora.',
    box: 'border-gray-200 bg-gray-50',
  },
};

function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `há ${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `há ${hours} h`;
  return `há ${Math.round(hours / 24)} d`;
}

/**
 * Saúde do sistema para o admin LEIGO: semáforo + erros recentes em linguagem simples +
 * botão "Acionar equipe técnica" (abre o WhatsApp com o resumo pronto para encaminhar).
 * O objetivo não é depurar — é saber que algo está errado e avisar o time certo.
 */
export function SystemHealthCard() {
  const [data, setData] = useState<SystemErrorsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi
      .get<SystemErrorsData>('/system-health/errors')
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="h-5 w-40 animate-pulse rounded bg-gray-100" />
      </div>
    );
  }

  if (!data || !data.configured) return null; // monitor não configurado → não polui o painel

  const ui = STATUS_UI[data.status];

  const report = [
    `🩺 ValiosasCRM — Saúde do sistema (${new Date().toLocaleString('pt-BR')})`,
    `${ui.icon} ${ui.title} — ${data.totalEvents24h} ocorrência(s) nas últimas 24h`,
    ...data.issues.slice(0, 5).map((i) => `• ${i.title} — ${i.count}x, última ${timeAgo(i.lastSeen)}`),
  ].join('\n');

  return (
    <div className={`rounded-xl border p-5 shadow-sm ${ui.box}`} data-testid="system-health-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Saúde do sistema</p>
          <p className="mt-1 text-lg font-bold text-gray-900">
            {ui.icon} {ui.title}
          </p>
          <p className="text-sm text-gray-600">{ui.sub}</p>
        </div>
        {data.status !== 'ok' && data.status !== 'unknown' && (
          <a
            href={`https://wa.me/?text=${encodeURIComponent(report)}`}
            target="_blank"
            rel="noreferrer"
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-gray-700"
          >
            Acionar equipe técnica
          </a>
        )}
      </div>

      {data.issues.length > 0 && (
        <ul className="mt-4 divide-y divide-gray-200/70">
          {data.issues.slice(0, 5).map((issue, idx) => (
            <li key={idx} className="flex items-center justify-between gap-3 py-2">
              <span className="min-w-0 truncate text-sm text-gray-800" title={issue.title}>
                {issue.title}
              </span>
              <span className="shrink-0 text-xs text-gray-500">
                {issue.count}x · {timeAgo(issue.lastSeen)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {data.trackerUrl && (
        <a
          href={data.trackerUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-block text-xs text-gray-500 underline hover:text-gray-700"
        >
          Ver detalhes técnicos (equipe)
        </a>
      )}
    </div>
  );
}
